import React, { useEffect, useMemo } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, useMap } from 'react-leaflet';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import 'leaflet/dist/leaflet.css';
import '../../../../utils/leafletDefaultIcons';
import { createMapPinIcon } from '../../../coffee_shops/components/MapPin';
import { CoffeeMapBasemap, COFFEE_MAP_CONTAINER_CLASS } from '../../../coffee_shops/components/CoffeeMapBasemap';
import type { Coordinates, CoffeeRateInterface } from '../types';

const InvalidateOnMount: React.FC = () => {
    const map = useMap();
    useEffect(() => {
        const t = window.setTimeout(() => map.invalidateSize(), 100);
        return () => window.clearTimeout(t);
    }, [map]);
    return null;
};

interface MapOverlayProps {
    coffeeData: CoffeeRateInterface;
    coordinates: Coordinates;
}

/**
 * Inline expanded map (covers the image-section). No × close — the action bar's
 * map thumb at the bottom-right toggles this view off. "Open in Google Maps"
 * pill sits at the bottom-left (out of the action-bar's path).
 */
export const MapOverlay: React.FC<MapOverlayProps> = ({ coffeeData, coordinates }) => {
    const pinIcon = useMemo(() => {
        const size = 28;
        return L.icon({
            iconUrl: createMapPinIcon(coffeeData.overallRating ?? 0, false),
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
            popupAnchor: [0, -size / 2],
        });
    }, [coffeeData.overallRating]);

    const openInGoogle = () => {
        const url = `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <motion.div
            className='coffee-map-inline-panel'
            role='region'
            aria-label='Coffee shop location map'
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
        >
            <MapContainer
                className={`${COFFEE_MAP_CONTAINER_CLASS} map-expanded-inline`}
                center={[coordinates.lat, coordinates.lng]}
                zoom={15}
                scrollWheelZoom
            >
                <CoffeeMapBasemap />
                <Marker position={[coordinates.lat, coordinates.lng]} icon={pinIcon} />
                <InvalidateOnMount />
            </MapContainer>
            <button
                type='button'
                className='coffee-map-open-google'
                onClick={openInGoogle}
                title='Open in Google Maps'
            >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
                <span>Open in Google</span>
            </button>
        </motion.div>
    );
};
